import React, { useEffect, useRef, useState } from 'react';
import useSeasonalAccessory from '../turbo/seasonal/useSeasonalAccessory';
import { useTurboParade } from '../turbo';
import { OTTO_SEASONS } from './ottoSeasons';
import { playHeadCock, COCK } from './ottoHeadCock';
import { subscribeGaze, gazeOffset } from './ottoGaze';
import OttoDust from './OttoDust';
import { ottoMotionSuppressed } from '../../lib/motion';
import './otto.css';

// The "he's alive" layer — an interactive EVERYDAY Otto (default/idle
// pose) that occasionally performs a subtle ambient motion, reacts to
// hover/click, follows the cursor with his pupils, and does a barrel
// roll on a secret input. Otto's permanent interactive home (intro card
// now, briefing bar later). Admin-only.
//
// All motion honours prefers-reduced-motion (then he's fully still) and
// disables hover + cursor-follow on touch (tap steps the animation
// cycle). Round-eye model rule holds: blink/yawn swap to closed arcs;
// pupils never resize. Animatable parts are class-based (not ids) so
// multiple instances never collide. Sources: otto-micro-animations.html,
// otto-flight-tricks.html.

// Was a local copy of the reduced-motion predicate. Now the shared one, which
// ORs in the per-user Otto toggle: every guard below reads correctly for both
// without a second condition at eight call sites.
const prefersReducedMotion = ottoMotionSuppressed;
const isTouch = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

// Idle pool — blink weighted heaviest; preen/goggle/yawn are rare treats.
// Sneeze sits in the pool ONCE — one entry in eleven is ~9%, close to the
// ~7% the brief recommends. (The prototype's weight of 22 ≈ 37% was far
// too often for a shot this loud.) Revisit only alongside the rest of the
// idle set, when the pool stops being blink-dominated.
const IDLE_POOL = ['blink', 'blink', 'blink', 'headtilt', 'stretch', 'scratch', 'shiver', 'preen', 'goggle', 'yawn', 'sneeze'];
// A click steps through every animation Otto does around the site.
const CLICK_CYCLE = ['wave', 'blink', 'headtilt', 'stretch', 'scratch', 'shiver', 'preen', 'goggle', 'perk', 'surprise', 'yawn', 'sneeze', 'cock', 'roll', 'settle'];
// `settle` is in CLICK_CYCLE (so it is demoable) but deliberately NOT in
// IDLE_POOL: it carries meaning. A quiet acknowledgement that fires on a timer,
// at nothing, is just a twitch -- and worse, it spends the one gesture reserved
// for bad news on a moment when nothing happened.
const DUR = { wave: 750, scratch: 750, headtilt: 1600, stretch: 1400, shiver: 600, perk: 520, surprise: 520, preen: 1560, goggle: 1620, roll: 1100, sneeze: 3000, settle: 1000 };
// Head-cock (build brief 05) is EVENT-DRIVEN — it belongs to a request in
// flight, so it stays out of IDLE_POOL and out of DUR: it has no fixed length,
// only in/loop/out phases (see ottoHeadCock.js). It's in the click cycle so
// the dev gallery can demo it. The real hosting decision resolved to
// SPLIT BY CONTEXT (brief §6, signed off 2026-08-07): OttoLoading's flying
// hover keeps the full-page loads where Otto is the focus, OttoInlineLoading
// takes inline/partial loads where a bar sits beside him to look at.

// A rare click on the briefing-bar Otto (≤ once per session) punches him
// out on an ejector seat instead of the usual animation.
const SESSION_EJECT_KEY = 'otto_ejected';

export default function OttoAlive({ size = 92, ariaLabel = 'Otto, the Supply Pilot mascot', season = null, interactive = true, ejectable = false, play = null, variant = 'everyday' }) {
  // Wardrobe. OttoAlive was everyday-only by construction while the dress
  // uniform lived in the static Otto.js, so anything wanting a captain had to
  // give up the idle loop, the click cycle and the cursor-follow to get it.
  //
  // A LAYER, NOT A POSE. The body geometry is identical — same jacket path,
  // same wings, same feet — so this swaps the torso decoration only, exactly as
  // Otto.js does. Nothing here changes what the rig can do.
  //
  // Restricted on purpose: only 'default' and 'waving' read correctly in dress
  // uniform, and this component is always upright, so there is no pose gate to
  // fail. See docs/otto-captain-uniform.md for why it stays scarce.
  const captain = variant === 'captain';
  // Seasonal accessory (default-pose surface). Resolved by the SHARED
  // machinery: an explicit `season` id (QA/gallery) wins; otherwise the
  // parade override, otherwise the calendar resolver (per-user opt-out).
  const seasonal = useSeasonalAccessory(OTTO_SEASONS);
  const { paradeActiveAccessoryId } = useTurboParade();
  const activeId = season ?? paradeActiveAccessoryId ?? seasonal?.id ?? null;
  const active = activeId
    ? (activeId === seasonal?.id ? seasonal : OTTO_SEASONS.find(s => s.id === activeId))
    : null;
  const Accessory = active?.Component || null;
  const capMode = !active?.replacesCap;

  const wrapRef = useRef(null);
  const eyesOpenRef = useRef(null);
  const eyesClosedRef = useRef(null);
  const pupilsRef = useRef(null);
  const svgRef = useRef(null);
  const blinkingRef = useRef(false);
  // Cursor-follow writes an inline transform on the pupils; the head-cock
  // drives them from CSS. Lock the follower out for the shot's duration so
  // the two don't fight over the gaze.
  const pupilsLockedRef = useRef(false);
  // What the gaze registry has resolved, if anything. Held in a ref rather than
  // state: it changes on scroll and must not re-render an owl.
  const gazeTargetRef = useRef(null);
  const clicksRef = useRef(0);
  const [ejecting, setEjecting] = useState(false);
  const [sneezing, setSneezing] = useState(false);

  const height = Math.round(size * 132 / 120);

  const setEyesClosed = (closed) => {
    if (eyesOpenRef.current) eyesOpenRef.current.style.opacity = closed ? '0' : '1';
    if (eyesClosedRef.current) eyesClosedRef.current.style.opacity = closed ? '1' : '0';
    blinkingRef.current = closed; // pauses cursor-follow while eyes are shut
  };
  const blink = () => {
    setEyesClosed(true);
    setTimeout(() => setEyesClosed(false), 120);
  };
  const yawnToSleep = () => {
    setEyesClosed(true);
    playWrap('yawn', 2160);
    setTimeout(() => setEyesClosed(false), 2160);
  };
  // Sneeze (build brief 05) — eyes screw shut just before the snap and
  // open on the recovery, per the round-eye rule (swap groups, never
  // scale pupils). The burst + dust are mounted for the shot's duration;
  // their own CSS delays fire them at the 840ms snap frame.
  const sneeze = () => {
    setSneezing(true);
    playWrap('sneeze', DUR.sneeze);
    setTimeout(() => setEyesClosed(true), 640);   // shut before the 780ms inhale peak
    setTimeout(() => setEyesClosed(false), 1150); // open on the recoil
    setTimeout(() => setSneezing(false), DUR.sneeze);
  };

  // Fire a one-shot wrap-class animation (removed after it plays so it
  // can replay). Part-level motions key off the same wrap class in CSS.
  const playWrap = (name, ms) => {
    const el = wrapRef.current;
    if (!el) return;
    el.classList.remove(`otto-anim-${name}`);
    void el.offsetWidth; // reflow so the animation restarts
    el.classList.add(`otto-anim-${name}`);
    setTimeout(() => el && el.classList.remove(`otto-anim-${name}`), ms);
  };

  // One dispatcher for every animation — shared by the idle loop, the
  // click cycle and the barrel-roll input.
  // Head-cock — runs the REAL in/loop/out driver rather than a fixed-length
  // look-alike, so the gallery demo can't drift from what ships. With no bar
  // to look at here the gaze falls back to up-and-right, and the demo holds
  // one full loop pass before unwinding. The cursor-follower is suspended and
  // the pupils' inline transform cleared first, or it fights the locked gaze.
  const headCock = () => {
    const el = wrapRef.current;
    if (!el) return;
    pupilsLockedRef.current = true;
    if (pupilsRef.current) pupilsRef.current.style.transform = '';
    const stop = playHeadCock(el);
    setTimeout(() => {
      stop().then(() => { pupilsLockedRef.current = false; });
    }, COCK.in + COCK.loop);
  };

  const runAnim = (kind) => {
    if (prefersReducedMotion()) return;
    if (kind === 'blink') { blink(); return; }
    if (kind === 'yawn') { yawnToSleep(); return; }
    if (kind === 'sneeze') { sneeze(); return; }
    if (kind === 'cock') { headCock(); return; }
    playWrap(kind, DUR[kind] || 1000);
  };

  // Imperative one-shot — the roaming overlay drops Otto somewhere and passes
  // a `play` name to fire a single animation (its own idle loop is off there,
  // interactive=false). runAnim already no-ops under reduced motion.
  useEffect(() => {
    if (!play) return;
    runAnim(play);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [play]);

  // QA handle for the BODY animations.
  //
  // Rob, 2026-08-14: "sneeze doesn't appear in my otto's shot list?" — it
  // doesn't, because there are two separate animation systems and the dev
  // panel only knew about one. The roamer's SHOTS are travelling overlay
  // pieces; these are body motions on a rendered Otto. Sneeze, yawn, preen and
  // the rest live here and were unreachable from the panel.
  //
  // Registered only for the INTERACTIVE instance — the persistent, clickable
  // Otto you can actually see. The roamer renders its own copy with
  // interactive=false and must not steal the handle. Last mount wins, which on
  // a normal page means the one in the bar.
  const runAnimRef = useRef(runAnim);
  runAnimRef.current = runAnim;
  useEffect(() => {
    if (!interactive) return undefined;
    window.__ottoAnim = (kind) => {
      if (!CLICK_CYCLE.includes(kind)) return `unknown motion; try: ${CLICK_CYCLE.join(', ')}`;
      runAnimRef.current(kind);
      return undefined;
    };
    window.__ottoAnims = CLICK_CYCLE;
    return () => {
      if (window.__ottoAnim) delete window.__ottoAnim;
      if (window.__ottoAnims) delete window.__ottoAnims;
    };
  }, [interactive]);

  // Ambient idle loop — one motion at a time, every ~8–16s.
  useEffect(() => {
    if (!interactive || prefersReducedMotion()) return undefined;
    let timer;
    const schedule = () => {
      timer = setTimeout(() => {
        runAnim(IDLE_POOL[Math.floor(Math.random() * IDLE_POOL.length)]);
        schedule();
      }, 8000 + Math.random() * 8000);
    };
    schedule();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive]);

  // Cursor-follow pupils — desktop only, off under reduced motion, paused
  // mid-blink. Pupils drift a few px toward the cursor within the socket.
  //
  // This is now the RESTING behaviour rather than the only one: when the gaze
  // registry has resolved a target, that wins and this yields. Cursor drift is
  // what he does when nothing more interesting is happening, which is most of
  // the time.
  useEffect(() => {
    if (!interactive || prefersReducedMotion() || isTouch()) return undefined;
    const onMove = (e) => {
      const svg = svgRef.current;
      const pupils = pupilsRef.current;
      if (!svg || !pupils || blinkingRef.current || pupilsLockedRef.current) return;
      if (gazeTargetRef.current) return;   // looking at something; not at you
      const r = svg.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height * 0.5;
      const dx = Math.max(-1, Math.min(1, (e.clientX - cx) / (r.width / 2)));
      const dy = Math.max(-1, Math.min(1, (e.clientY - cy) / (r.height / 2)));
      pupils.style.transform = `translate(${(dx * 2.4).toFixed(2)}px, ${(dy * 2).toFixed(2)}px)`;
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [interactive]);

  // Targeted gaze. Points the pupils at whatever the registry has resolved, and
  // returns them to rest when it resolves nothing.
  //
  // Yields to everything that owns the eyes deliberately:
  //   · pupilsLockedRef — the loading head-cock locks the gaze on the progress
  //     bar, and that shot's whole premise is that the head turns while the
  //     gaze does not.
  //   · blinkingRef — same rule cursor-follow already respects.
  //   · interactive=false — the roaming overlay's Otto, whose shots script
  //     their own eye movement (the trio's mutual looks, the peek's scan).
  //
  // Re-measured on scroll and resize, since a target's position on screen is
  // what he is looking at, not its position in the document.
  useEffect(() => {
    if (!interactive || prefersReducedMotion() || isTouch()) return undefined;
    let raf = 0;
    const paint = () => {
      raf = 0;
      const svg = svgRef.current;
      const pupils = pupilsRef.current;
      if (!svg || !pupils || blinkingRef.current || pupilsLockedRef.current) return;
      const el = gazeTargetRef.current;
      if (!el || !el.isConnected) {
        // Back to rest. Looking away is less urgent than looking, so it eases
        // out more slowly than it came in.
        pupils.style.transitionDuration = '320ms';
        pupils.style.transform = '';
        return;
      }
      pupils.style.transitionDuration = '';   // the stylesheet's 180ms
      const { x, y } = gazeOffset(svg.getBoundingClientRect(), el.getBoundingClientRect());
      pupils.style.transform = `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`;
    };
    const schedule = () => { if (!raf) raf = window.requestAnimationFrame(paint); };
    const off = subscribeGaze((el) => { gazeTargetRef.current = el; schedule(); });
    window.addEventListener('scroll', schedule, { passive: true, capture: true });
    window.addEventListener('resize', schedule);
    return () => {
      off();
      if (raf) window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      gazeTargetRef.current = null;
    };
  }, [interactive]);

  // Secret input — typing "do a barrel roll" anywhere makes Otto roll.
  useEffect(() => {
    if (!interactive) return undefined;
    let typed = '';
    const onKey = (e) => {
      if (!e.key || e.key.length !== 1) return;
      typed = (typed + e.key.toLowerCase()).slice(-16);
      if (typed.endsWith('do a barrel roll')) { typed = ''; runAnim('roll'); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive]);

  // Hover = perk (desktop, non-reduced). Touch has no hover.
  const handleEnter = () => {
    if (!interactive || prefersReducedMotion() || isTouch()) return;
    playWrap('perk', 520);
  };
  // Rare ejector-seat treat — only where enabled (briefing bar), never
  // under reduced motion, and at most once per session. Returns true when
  // it fires so the normal click cycle is skipped this time.
  const maybeEject = () => {
    if (!ejectable || prefersReducedMotion()) return false;
    try { if (sessionStorage.getItem(SESSION_EJECT_KEY) === '1') return false; } catch (e) { return false; }
    if (Math.random() >= 0.2) return false;
    try { sessionStorage.setItem(SESSION_EJECT_KEY, '1'); } catch (e) { /* ignore */ }
    setEjecting(true);
    playWrap('eject', 1900);
    setTimeout(() => setEjecting(false), 1900);
    return true;
  };
  // Click (or tap) steps through every animation, one per click.
  const handleClick = () => {
    if (!interactive || prefersReducedMotion()) return;
    if (maybeEject()) return;
    runAnim(CLICK_CYCLE[clicksRef.current % CLICK_CYCLE.length]);
    clicksRef.current += 1;
  };

  return (
    <div
      ref={wrapRef}
      className="otto-alive-wrap"
      onMouseEnter={handleEnter}
      onClick={handleClick}>
      {ejecting && (
        <span className="otto-chute" aria-hidden="true">
          <svg width="52" height="38" viewBox="0 0 52 38">
            <path d="M3 15 a23 14 0 0 1 46 0 z" fill="var(--scarf)" />
            <path d="M3 15 a23 14 0 0 1 46 0" fill="none" stroke="rgba(0,0,0,.14)" strokeWidth="1" />
            <path d="M13 6 v9 M26 3 v12 M39 6 v9" stroke="rgba(0,0,0,.1)" strokeWidth="1" />
            <path d="M9 14 q6 10 8 18 M26 15 v17 M43 14 q-6 10 -8 18" stroke="#6b5636" strokeWidth="1.4" fill="none" />
          </svg>
        </span>
      )}
      {sneezing && (
        <span className="otto-sneeze-fx" aria-hidden="true">
          {/* Ported from the prototype's #p-sneeze prop, geometry intact. */}
          <svg viewBox="0 0 60 44">
            <g stroke="#98A08F" strokeWidth="2.6" strokeLinecap="round">
              <line x1="2" y1="22" x2="20" y2="22" />
              <line x1="4" y1="12" x2="18" y2="6" />
              <line x1="4" y1="32" x2="18" y2="38" />
            </g>
            <circle cx="30" cy="14" r="2.6" fill="#A8CBD6" />
            <circle cx="36" cy="28" r="2.1" fill="#A8CBD6" />
            <circle cx="46" cy="19" r="1.7" fill="#A8CBD6" />
          </svg>
        </span>
      )}
      {sneezing && <OttoDust delay="0.95s" />}
      <svg ref={svgRef} className="otto-svg" viewBox="0 0 120 132" width={size} height={height} role="img" aria-label={ariaLabel}>
        <ellipse cx="60" cy="76" rx="34" ry="40" fill="var(--brown)" />
        {/* Ear tufts show when a full-hat accessory replaces the cap. */}
        {!capMode && (
          <>
            <path d="M40 40 l3 13 l7 -7 z" fill="var(--brown-d)" />
            <path d="M80 40 l-3 13 l-7 -7 z" fill="var(--brown-d)" />
          </>
        )}
        <path d="M27 78 q0 26 12 38 q21 8 42 0 q12 -12 12 -38 q-33 14 -66 0z" fill={captain ? 'var(--navy)' : 'var(--jacket)'} />
        {!captain && <path d="M60 82 v34" stroke="var(--jacket-d)" strokeWidth="2.5" />}
        <path className="otto-part-lwing" d="M27 80 q-8 16 3 30 q7 -9 8 -24z" fill="var(--brown-d)" />
        <path className="otto-part-rwing" d="M93 80 q8 16 -3 30 q-7 -9 -8 -24z" fill="var(--brown-d)" />
        {/* gold wing pin */}
        <g transform="translate(60 93)">
          <path d="M-2.6 -4 h5.2 v3.5 q0 3.5 -2.6 5 q-2.6 -1.5 -2.6 -5z" fill="var(--gold)" />
          <path d="M-3 -2 q-6 -1 -10 0 q-4 0 -7 1 q3 .6 6 .6 q-3 .8 -6 1 q3 .5 6 .4 q-3 .9 -5 1.4 q6 .8 12 -1.2 q3 -1 4 -3.2z" fill="var(--gold)" />
          <path d="M3 -2 q6 -1 10 0 q4 0 7 1 q-3 .6 -6 .6 q3 .8 6 1 q-3 .5 -6 .4 q3 .9 5 1.4 q-6 .8 -12 -1.2 q-3 -1 -4 -3.2z" fill="var(--gold)" />
        </g>
        {captain && (
          <>
            <path d="M60 80 l-16 6 l4 10 l12 -8z" fill="var(--navy-l)" />
            <path d="M60 80 l16 6 l-4 10 l-12 -8z" fill="var(--navy-l)" />
            <path d="M60 80 l-8 22 h16z" fill="var(--shirt)" />
            <path d="M60 84 l-4 6 l4 16 l4 -16z" fill="var(--tie)" />
            <path d="M56 90 l4 -6 l4 6 l-4 4z" fill="var(--tie-d)" />
            <rect x="29" y="82" width="13" height="7" rx="2" fill="var(--navy-d)" />
            <path d="M31 84 h9 M31 86 h9 M31 88 h9" stroke="var(--gold)" strokeWidth="1" />
            <rect x="78" y="82" width="13" height="7" rx="2" fill="var(--navy-d)" />
            <path d="M80 84 h9 M80 86 h9 M80 88 h9" stroke="var(--gold)" strokeWidth="1" />
          </>
        )}
        {/* The fleece collar is everyday only — the dress uniform has a shirt
            and tie in its place, and drawing both reads as a scarf over a suit. */}
        {!captain && <path d="M34 76 q10 12 26 12 q16 0 26 -12 q-8 -2 -14 4 q-12 8 -24 0 q-6 -6 -14 -4z" fill="var(--fleece)" />}
        <g strokeLinecap="round"><circle cx="52" cy="114" r="3.4" fill="var(--amber)" /><path d="M52 114 L46 125 M52 114 L52 127 M52 114 L58 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
        <g strokeLinecap="round"><circle cx="68" cy="114" r="3.4" fill="var(--amber)" /><path d="M68 114 L62 125 M68 114 L68 127 M68 114 L74 125" stroke="var(--amber)" strokeWidth="4.2" fill="none" /></g>
        {!captain && (
          <>
            <path className="otto-part-scarf" d="M40 74 q20 12 40 0 q2 6 -2 9 q-18 10 -36 0 q-4 -3 -2 -9z" fill="var(--scarf)" />
            <path className="otto-part-scarftail" d="M78 80 q14 4 20 16 q-4 5 -10 3 q-8 -6 -12 -13z" fill="var(--scarf)" />
          </>
        )}
        {/* HEAD GROUP. Everything from the eyes down to the cap and goggles is
            already drawn contiguously, so wrapping it changes no z-order and
            nothing visual — it exists so the head can move as a unit.

            `settle` needs a forward DIP rather than the sideways tilt a flat
            sprite is otherwise stuck with, because a Z-rotation reads as
            curiosity and that meaning is already taken by `headtilt` and the
            loading head-cock. A small translate on this group is the nearest
            honest thing to a nod that 2D allows.

            The seasonal accessory is deliberately INSIDE it: a hat that stayed
            put while the head moved would read as a rendering fault. */}
        <g className="otto-part-head">
        {/* Classed as well as ref'd: the ledge shot (09) cross-fades these
            from CSS, and a CSS animation outranks the inline opacity below. */}
        <g ref={eyesOpenRef} className="otto-eyes-open">
          <circle cx="48" cy="66" r="14" fill="#fbfdf6" /><circle cx="72" cy="66" r="14" fill="#fbfdf6" />
          <g ref={pupilsRef} className="otto-pupils">
            <circle cx="50" cy="66" r="7.5" fill="var(--fusion-green)" /><circle cx="70" cy="66" r="7.5" fill="var(--fusion-green)" />
            <circle cx="50" cy="66" r="3.8" fill="#17170f" /><circle cx="70" cy="66" r="3.8" fill="#17170f" />
            <circle cx="52" cy="64" r="1.7" fill="#fff" /><circle cx="72" cy="64" r="1.7" fill="#fff" />
          </g>
        </g>
        <g ref={eyesClosedRef} className="otto-eyes-closed" style={{ opacity: 0 }}>
          <circle cx="48" cy="66" r="14.5" fill="var(--brown)" /><circle cx="72" cy="66" r="14.5" fill="var(--brown)" />
          <path d="M41 66 q7 6 14 0" stroke="#3a2c1a" strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M65 66 q7 6 14 0" stroke="#3a2c1a" strokeWidth="3" fill="none" strokeLinecap="round" />
        </g>
        <path className="otto-part-beak" d="M60 72 l-5 7 h10 z" fill="var(--amber)" />
        {capMode ? (
          <>
            <path d="M30 54 a30 26 0 0 1 60 0 q-30 -12 -60 0 z" fill="var(--cap)" />
            <path d="M30 54 q30 -12 60 0" stroke="var(--cap-d)" strokeWidth="2.5" fill="none" />
            <path d="M31 52 q-5 10 -1 20 q7 2 9 -3 q-4 -9 -2 -18 z" fill="var(--cap)" />
            <path d="M89 52 q5 10 1 20 q-7 2 -9 -3 q4 -9 2 -18 z" fill="var(--cap)" />
            <g className="otto-part-goggles">
              <circle cx="47" cy="50" r="7" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="3" />
              <circle cx="73" cy="50" r="7" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="3" />
              <path d="M54 50 h12" stroke="#2c2c22" strokeWidth="3" />
            </g>
          </>
        ) : (
          <g className="otto-part-goggles">
            {/* Goggles kept on the brow when the cap is replaced by a hat. */}
            <circle cx="47" cy="52" r="7" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="3" />
            <circle cx="73" cy="52" r="7" fill="#bfe3ea" stroke="#2c2c22" strokeWidth="3" />
            <path d="M54 52 h12" stroke="#2c2c22" strokeWidth="3" />
          </g>
        )}
        {Accessory && <Accessory />}
        </g>
      </svg>
    </div>
  );
}
